import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useData } from '../contexts/DataContext';
import { useT, translateWorkType } from '../lib/i18n';
import { generateInvoicePDF } from '../lib/pdf';
import { supabase } from '../lib/supabaseClient';
import { displayNumber, isNumberPending } from '../lib/numbering';
import { ArrowLeft, Edit2, Trash2, Download, Printer, Eye, Mail } from 'lucide-react';
import { toast } from 'sonner';

const money = (n) => (Number(n) || 0).toFixed(2) + ' $';

function Row({ label, value, bold, muted }) {
  return (
    <div className={`flex items-center justify-between py-1.5 ${muted ? 'text-sm text-gray-600' : ''}`}>
      <span className={bold ? 'font-bold' : ''}>{label}</span>
      <span className={bold ? 'font-bold' : ''}>{value}</span>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { getInvoice, removeInvoice, profile, saveInvoice } = useData();
  const { t, lang } = useT();
  const [inv, setInv] = useState(null);

  useEffect(() => {
    getInvoice(id).then(setInv);
  }, [id, getInvoice]);

  if (!inv) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
      </Layout>
    );
  }

  const s = inv.summary || {};
  const materials = Array.isArray(inv.materials) ? inv.materials : [];

  const onDelete = async () => {
    if (!window.confirm(`${t('hist.delete')} ${displayNumber(inv.invoice_number, t)} ?`)) return;
    await removeInvoice(inv.id);
    toast.success('✓');
    nav('/invoices');
  };

  const onEmailPdf = async () => {
    if (isNumberPending(inv.invoice_number)) {
      toast.error(t('common.number_pending_block_send'));
      return;
    }
    if (!inv.client_email || !inv.client_email.trim()) {
      toast.error(lang === 'fr' ? 'Ajoutez un courriel au client' : 'Add a client email');
      return;
    }
    if (!navigator.onLine) {
      toast.error(lang === 'fr'
        ? 'Hors ligne — l\u2019envoi par courriel nécessite une connexion internet.'
        : 'Offline — sending by email requires an internet connection.');
      return;
    }
    const tId = toast.loading(lang === 'fr' ? 'Préparation du PDF…' : 'Preparing the PDF…');
    try {
      const blob = await generateInvoicePDF(inv, profile, t, { returnBlob: true, lang });
      if (!blob) {
        toast.dismiss(tId);
        toast.error(lang === 'fr' ? 'Échec génération PDF' : 'PDF generation failed');
        return;
      }
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result || '';
          const comma = String(dataUrl).indexOf(',');
          resolve(comma > -1 ? String(dataUrl).slice(comma + 1) : String(dataUrl));
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        toast.dismiss(tId);
        toast.error(lang === 'fr' ? 'Ta session a expiré. Reconnecte-toi.' : 'Your session expired.');
        return;
      }
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const axios = (await import('axios')).default;
      await axios.post(`${API}/email/send-pdf`, {
        recipient_email: inv.client_email.trim(),
        client_name: inv.client_name || '',
        company_name: profile?.company_name || '',
        submission_number: inv.invoice_number,
        lang,
        grand_total: inv.summary?.grandTotal || 0,
        pdf_base64: pdfBase64,
        filename: `${inv.invoice_number || 'facture'}.pdf`,
        doc_type: 'invoice',
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (inv.status === 'draft') {
        const updated = await saveInvoice({ ...inv, status: 'sent' });
        if (updated) setInv(updated);
      }
      toast.dismiss(tId);
      toast.success(lang === 'fr' ? '✓ Facture envoyée au client' : '✓ Invoice sent to client');
    } catch (e) {
      toast.dismiss(tId);
      const detail = e?.response?.data?.detail || '';
      const code = e?.response?.status;
      if (code === 401) {
        toast.error(lang === 'fr' ? 'Ta session a expiré. Reconnecte-toi.' : 'Your session expired.');
      } else if (code === 402) {
        toast.error(lang === 'fr' ? 'Ton essai gratuit est terminé. Souscris pour envoyer des courriels.' : 'Your free trial has ended.');
      } else if (code === 413) {
        toast.error(lang === 'fr' ? 'PDF trop volumineux pour être envoyé.' : 'PDF too large to send.');
      } else if (/RESEND_API_KEY/i.test(detail)) {
        toast.error(lang === 'fr' ? 'Email non configuré (clé Resend manquante).' : 'Email not configured (Resend key missing).');
      } else {
        toast.error(lang === 'fr' ? "Échec de l\u2019envoi : " + (detail || e.message) : 'Send failed: ' + (detail || e.message));
      }
    }
  };

  return (
    <Layout title={t('invoice.detail_title')}>
      <Link to="/invoices" className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 mb-4">
        <ArrowLeft size={16} /> {t('auth.back')}
      </Link>

      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className={`font-mono text-sm mb-1 ${isNumberPending(inv.invoice_number) ? 'italic text-amber-600' : 'text-gray-500'}`}>
            {displayNumber(inv.invoice_number, t)}
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">{inv.client_name || '—'}</h1>
          <div className="text-sm text-gray-500 mt-1">{translateWorkType(inv.work_type, lang)}</div>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#FAF7F2] text-[#7A1F2B]">
          {t(`invoice.status.${inv.status || 'draft'}`)}
        </span>
      </div>

      {inv.source_submission_number && (
        <div className="mb-4 p-3 rounded-xl border text-sm" style={{ borderColor: '#E5DCC9', background: '#FAF7F2' }}>
          {t('invoice.from_submission').replace('{num}', inv.source_submission_number)}
          {inv.source_submission_id && (
            <Link
              to={`/submissions/${inv.source_submission_id}`}
              className="ml-2 font-bold text-[#7A1F2B] underline"
              data-testid="invoice-source-link"
            >
              {t('hist.view')}
            </Link>
          )}
        </div>
      )}

      <div className="card p-4 mb-4 space-y-1 text-sm">
        <Row label={t('invoice.date')} value={inv.invoice_date || '—'} />
        <Row label={t('invoice.due_date')} value={inv.due_date || '—'} />
        {inv.site_address && <Row label={t('invoice.site_address')} value={inv.site_address} muted />}
        {inv.client_email && <Row label={t('auth.email')} value={inv.client_email} muted />}
        {inv.client_phone && <Row label={lang === 'fr' ? 'Téléphone' : 'Phone'} value={inv.client_phone} muted />}
      </div>

      {materials.length > 0 && (
        <div className="card p-4 mb-4">
          <div className="font-bold mb-2 text-sm uppercase tracking-wide text-[#7A1F2B]">
            {lang === 'fr' ? 'Matériaux' : 'Materials'}
          </div>
          <div className="space-y-2">
            {materials.map((m) => (
              <div key={m.id || m.description} className="flex justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="font-medium truncate">{m.description || '—'}</div>
                  <div className="text-xs text-gray-500">
                    {m.quantity} × {money(m.unit_price)}
                  </div>
                </div>
                <div className="font-bold shrink-0">
                  {money((Number(m.quantity) || 0) * (Number(m.unit_price) || 0))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card p-4 mb-4">
        <Row label={t('sum.subtotal')} value={money(s.subtotal)} bold />
        {(s.taxes || []).map((tx) => (
          <Row key={tx.label} label={tx.label} value={money(tx.amount)} muted />
        ))}
        <div className="flex items-center justify-between pt-3 border-t-2 border-gray-200 mt-2">
          <span className="font-heading font-black text-lg">{t('sum.grand')}</span>
          <span className="font-heading font-black text-2xl">{money(s.grandTotal)}</span>
        </div>
      </div>

      {(inv.notes || inv.conditions) && (
        <div className="card p-4 mb-4 space-y-3 text-sm">
          {inv.notes && (
            <div>
              <div className="font-bold text-[#7A1F2B] mb-1">{t('invoice.notes')}</div>
              <div className="whitespace-pre-wrap text-gray-700">{inv.notes}</div>
            </div>
          )}
          {inv.conditions && (
            <div>
              <div className="font-bold text-[#7A1F2B] mb-1">{t('invoice.conditions')}</div>
              <div className="whitespace-pre-wrap text-gray-700">{inv.conditions}</div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mb-6">
        <button
          data-testid="invoice-preview-pdf-btn"
          onClick={async () => {
            const tId = toast.loading(lang === 'fr' ? 'Génération de l\u2019aperçu…' : 'Generating preview…');
            const ok = await generateInvoicePDF(inv, profile, t, { preview: true, lang });
            toast.dismiss(tId);
            if (!ok) toast.error(lang === 'fr' ? 'Échec de l\u2019aperçu' : 'Preview failed');
          }}
          className="btn-accent gap-2 col-span-2"
        >
          <Eye size={18} /> {t('invoice.preview')}
        </button>
        <button
          data-testid="invoice-download-pdf"
          onClick={() => generateInvoicePDF(inv, profile, t, { lang })}
          className="btn-primary gap-2"
        >
          <Download size={18} /> PDF
        </button>
        <button
          data-testid="invoice-print-btn"
          onClick={() => generateInvoicePDF(inv, profile, t, { print: true, lang })}
          className="btn-primary gap-2"
        >
          <Printer size={18} /> {t('common.print')}
        </button>
        <button
          data-testid="invoice-email-pdf-btn"
          onClick={onEmailPdf}
          className="btn-primary gap-2 col-span-2"
        >
          <Mail size={18} /> {t('invoice.send')}
        </button>
        <Link
          to={`/invoices/${inv.id}/edit`}
          data-testid="invoice-edit-btn"
          className="btn-outline gap-2 col-span-2"
        >
          <Edit2 size={18} /> {t('invoice.edit')}
        </Link>
        <button
          data-testid="invoice-delete-btn"
          onClick={onDelete}
          className="btn-outline gap-2 !text-red-700 !border-red-300 col-span-2"
        >
          <Trash2 size={18} /> {t('hist.delete')}
        </button>
      </div>
    </Layout>
  );
}
